import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Plus } from "lucide-react";
import {
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { getOrganizationUser } from "../../Apis/Organization-User";
import useAuthStore from "../../Zustand/Store/useAuthStore";
import axios from "axios";
import { MAIN_URL, getApiHeaders } from "../../Configurations/Urls";
import usePermissionDataStore from "../../Zustand/Store/usePermissionDataStore";
import NotAllowed from "../../Exceptions/NotAllowed";
import TableDataGeneric from "../../Configurations/TableDataGeneric";
import { operatorRequiresValue } from "../../Components/filters/operators";
import {
  USER_DEFAULT_COLUMNS,
  USERS_DATAGRID_KEY,
  mapSavedUserColumns,
  mapUserRowForDisplay,
  resolveUserApiField,
} from "./userListConstants";

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "application_user_full_name",
  sortOrder: "asc",
};

const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    )
      return;

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;

      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          filterValue = filterValue
            .split(",")
            .map((v) => v.trim().toLowerCase());
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }

      filters.push({
        field: resolveUserApiField(field),
        operator,
        value: filterValue,
      });
    }
  });

  return filters;
};

function Users() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [search, setSearch] = useState("");
  const { userData } = useAuthStore();
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);
  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(USER_DEFAULT_COLUMNS);
  const [loadingConfig, setLoadingConfig] = useState(true);

  const org = userData?.organization;
  const [filterStatus, setFilterStatus] = useState("all");
  const [deleteDialog, setDeleteDialog] = useState(null);
  const { Permission, setPermission } = usePermissionDataStore();
  const [showNotAllowed, setShowNotAllowed] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);
  const serverSearchDebounceRef = useRef(null);
  const hasLoadedUsersOnceRef = useRef(false);

  // Permission checks
  const hasAddPermission = useMemo(() => {
    return Permission && Array.isArray(Permission) && Permission.includes("USER_ADD");
  }, [Permission]);

  const hasEditPermission = useMemo(() => {
    return Permission && Array.isArray(Permission) && Permission.includes("USER_EDIT");
  }, [Permission]);

  const hasDeletePermission = useMemo(() => {
    return Permission && Array.isArray(Permission) && Permission.includes("USER_DELETE");
  }, [Permission]);

  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!org?.organization_id) {
        setLoadingConfig(false);
        return;
      }
      try {
        const datagridKey = `${USERS_DATAGRID_KEY}_${org.organization_id}`;
        const configRes = await fetch(`${MAIN_URL}/api/datagrid/get-by-context`, {
          method: "POST",
          headers: getApiHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify({
            datagrid_key: datagridKey,
            organization_id: org.organization_id,
          }),
        });

        if (configRes.ok) {
          const configResponse = await configRes.json();
          const serverCfg =
            configResponse?.datagrid?.datagrid_default_configuration;

          if (serverCfg?.columns?.length > 0) {
            setTableConfig(serverCfg);
            setConfigColumns(mapSavedUserColumns(serverCfg.columns));
          } else {
            setTableConfig(null);
            setConfigColumns(USER_DEFAULT_COLUMNS);
          }
        } else {
          setTableConfig(null);
          setConfigColumns(USER_DEFAULT_COLUMNS);
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setTableConfig(null);
        setConfigColumns(USER_DEFAULT_COLUMNS);
      } finally {
        setLoadingConfig(false);
      }
    };
    loadTableConfiguration();
  }, [org?.organization_id]);

  // Initialize URL parameters
  useEffect(() => {
    if (hasInitializedURL.current) return;
    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;
    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });
    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }
    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  const handleDeleteConfirm = async () => {
    if (!deleteDialog) return;
    if (!hasDeletePermission) {
      setDeleteDialog(null);
      setShowNotAllowed(true);
      return;
    }

    try {
      // Call the delete API
      await axios.delete(
        `${MAIN_URL}/api/organizations/${org.organization_id}/user/${deleteDialog}`,  { headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },}
      );

      // Remove user from local state after successful deletion
      setUsers((prev) =>
        prev.filter((u) => u.organization_user_id !== deleteDialog)
      );
      setDeleteDialog(null);
      toast.success("User deleted successfully");
      window.location.reload();
    } catch (err) {
      console.error(err);
      if (err.response?.status === 422) {
        const validationErrors =
          err.response.data.messages ||
          err.response.data.error ||
          "Validation failed.";
        toast.error(
          Array.isArray(validationErrors)
            ? validationErrors.join(" ")
            : validationErrors
        );
      } else {
        toast.error("Something went wrong while deleting user.");
        setDeleteDialog(null);
      }
    }
  };




  const ToggleStatusTable = async (user, newStatus) => {
    try {
      await axios.put(
        `${MAIN_URL}/api/organizations/${org.organization_id}/user/${user.organization_user_id}/toggle`,
        { is_active: newStatus },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      toast.success(
        `User status updated to ${newStatus ? "Active" : "Inactive"}`
      );
      setUsers((prevUsers) =>
        prevUsers.map((u) =>
          u.organization_user_id === user.organization_user_id
            ? { ...u, is_active: newStatus ? 1 : 0 }
            : u
        )
      );
    } catch (error) {
      console.error("Failed to update user status", error);
      toast.error("Failed to update user status");
      throw error;
    }
  };



useEffect(() => {
  const handler = setTimeout(() => {
    setDebouncedSearch(search);
  }, 500); // 500ms delay

  return () => clearTimeout(handler);
}, [search]);




  const ToggleStatus = async (userId, is_active, id) => {
    console.log("userid", userId);
    console.log("New Status:", is_active);
    try {
      const response = await axios.put(
        `${MAIN_URL}/api/organizations/${org.organization_id}/user/${userId}`,
        { is_active },  { headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },}
      );

      setUsers((prev) =>
        prev.map((u, i) => (i === id ? { ...u, is_active } : u))
      );

      toast.success("User status updated.");
    } catch (error) {
      console.error(error);
      const message =
        error.response?.data?.error ||
        "An error occurred while updating status.";
      toast.error(message);
    }
  };

// useEffect(() => {

//   if (!org?.organization_id) return;
//   setLoading(true);

//   const fetchUsers = async () => {
//     try {
//       const data = await getOrganizationUser(org?.organization_id, search, filterStatus);
//       console.log("data is ", data)
//       setFilteredUsers(data.users);
//     } catch (error) {
//       console.error("Failed to fetch users:", error);
//     } finally {
//       setLoading(false);
//     }
//   };
//   fetchUsers();
// }, [org?.organization_id]);



// useEffect(() => {
//   if (!org?.organization_id) return;
//   const fetchUsers = async () => {
//     try {
   
//        const data = await getOrganizationUser(org?.organization_id);
//       console.log("saysgugugugugugs", data)
//       setFilteredUsers(data?.users);
//     } catch (error) {
//       console.error("Failed to fetch users:", error);
//     }
//   };
//   fetchUsers();
// }, [search, filterStatus, org?.organization_id]);



// useEffect(() => {
//   if (!org?.organization_id) return;

//   const fetchUsers = async () => {
//     try {
//       setLoading(true);

//       const data = await getOrganizationUser(
//         org?.organization_id,
//         debouncedSearch,     // ⬅ IMPORTANT
//         filterStatus
//       );

//       setFilteredUsers(data?.users || []);
//     } catch (error) {
//       console.error("Failed to fetch users:", error);
//     } finally {
//       setLoading(false);
//     }
//   };

//   fetchUsers();
// }, [org?.organization_id, debouncedSearch, filterStatus]);






  useEffect(() => {
    if (!org?.organization_id) return;

    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();

    if (!hasLoadedUsersOnceRef.current) {
      setInitialLoading(true);
    } else {
      setFetching(true);
    }

    const page = Number.parseInt(searchParams.get("page"), 10) || 1;
    const pageSize = Number.parseInt(searchParams.get("pageSize"), 10) || 10;

    const fetchData = async () => {
      try {
        const filters = buildFiltersFromParams(searchParams);
        const search = searchParams.get("search");

        const requestBody = {
          filters,
          page,
          pageSize,
          sort: {
            field: resolveUserApiField(
              searchParams.get("sortBy") || "application_user_full_name"
            ),
            direction: searchParams.get("sortOrder") || "asc",
          },
          ...(search && search.trim() !== "" && { search: search.trim() }),
        };

        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/user/filtered`,
          {
            method: "POST",
            headers: getApiHeaders(),
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        let data;
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            data = result.data;

            const pag = result.pagination;
            const limit = pag?.pageSize ?? pag?.limit ?? pageSize;
            const total = pag?.total ?? 0;
            const totalPages = Math.max(
              1,
              pag?.totalPages ?? (total > 0 ? Math.ceil(total / limit) : 1)
            );

            setPaginationData({
              page: pag?.page ?? page,
              pageSize: limit,
              total,
              totalPages,
              hasNext: Boolean(pag?.hasNext ?? page < totalPages),
              hasPrev: Boolean(pag?.hasPrev ?? page > 1),
            });
          } else {
            data = [];
            setPaginationData({ ...DEFAULT_PAGINATION, page, pageSize });
          }
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const mapped = (data || []).map((item) => ({
          ...item,
          id: item.organization_user_id,
        }));

        setUsers(mapped);
      } catch (err) {
        if (err.name === "AbortError") return;

        console.error("Failed to fetch users:", err);
        toast.error("Failed to fetch users");
        if (!hasLoadedUsersOnceRef.current) {
          setUsers([]);
          setPaginationData({ ...DEFAULT_PAGINATION, page, pageSize });
        }
      } finally {
        setInitialLoading(false);
        setFetching(false);
        hasLoadedUsersOnceRef.current = true;
      }
    };

    fetchData();

    return () => {
      fetchControllerRef.current?.abort();
    };
  }, [org?.organization_id, searchParams.toString()]);

  const handleSortChange = useCallback(
    (field, direction) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        const currentField =
          prev.get("sortBy") || "application_user_full_name";
        const currentDirection = prev.get("sortOrder") || "asc";

        // Only reset to page 1 if the sort field/direction actually changed.
        const apiField = resolveUserApiField(field);
        if (currentField !== apiField || currentDirection !== direction) {
          next.set("sortBy", apiField);
          next.set("sortOrder", direction);
          next.set("page", "1");
        }

        return next;
      }, { replace: true });
    },
    [setSearchParams]
  );

  const handleFilterChange = useCallback(
    (filtersOrUpdates) => {
      setSearchParams((prev) => {
        const prevParams = new URLSearchParams(prev);
        const next = new URLSearchParams(prev);

        const buildFilterSignature = (params) =>
          [...params.keys()]
            .filter((k) => k.startsWith("filter_") || k === "search")
            .sort()
            .map((k) => `${k}=${params.get(k) ?? ""}`)
            .join("&");

        const prevSignature = buildFilterSignature(prevParams);

        if (Array.isArray(filtersOrUpdates)) {
          [...next.keys()]
            .filter((k) => k.startsWith("filter_"))
            .forEach((k) => next.delete(k));
          filtersOrUpdates.forEach((f) => {
            if (f?.field != null) {
              next.set(`filter_${f.field}`, f.value ?? "");
              if (f.operator) next.set(`filter_${f.field}_operator`, f.operator);
            }
          });
        } else if (
          filtersOrUpdates &&
          typeof filtersOrUpdates === "object" &&
          filtersOrUpdates.searchQuery !== undefined
        ) {
          if (String(filtersOrUpdates.searchQuery).trim() !== "") {
            next.set("search", filtersOrUpdates.searchQuery.trim());
          } else {
            next.delete("search");
          }
        }

        const nextSignature = buildFilterSignature(next);

        // Only reset to page 1 if filters or search actually changed.
        if (prevSignature !== nextSignature) {
          next.set("page", "1");
        }

        return next;
      }, { replace: true });
    },
    [setSearchParams]
  );

  const handlePageChange = useCallback(
    (page) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("page", String(page));
        if (!next.has("pageSize")) next.set("pageSize", "10");
        return next;
      }, { replace: true });
    },
    [setSearchParams]
  );

  const handleServerSearchChange = useCallback(
    (value) => {
      if (serverSearchDebounceRef.current) {
        clearTimeout(serverSearchDebounceRef.current);
      }
      serverSearchDebounceRef.current = setTimeout(() => {
        handleFilterChange({ searchQuery: value });
      }, 400);
    },
    [handleFilterChange]
  );

  useEffect(
    () => () => {
      if (serverSearchDebounceRef.current) {
        clearTimeout(serverSearchDebounceRef.current);
      }
    },
    []
  );

  // Table data: use `users` (from POST /user/filtered). Support both flat API shape and legacy nested shape.
  const tableRows = useMemo(
    () => users.map((user) => mapUserRowForDisplay(user)),
    [users]
  );

  const handleEdit = (row) => {
    navigate(`/users/edit/${row.organization_user_id}`);
  };

  const deleteUser = (id) => {
    // Use existing dialog flow
    setDeleteDialog(id);
  };

if(showNotAllowed)
{
  return <NotAllowed/>
}
  return (
    <div className="ems-fade-up">
      <div className="ems-page-head">
        <div>
          <h1 className="ems-page-title">Users Management</h1>
          <div className="ems-page-sub">
            <span>User management</span>
            <span className="ems-dot" />
            <span>{paginationData.total} users</span>
          </div>
        </div>
        <div className="ems-head-actions">
          {hasAddPermission && (
            <button className="ems-btn ems-primary" onClick={() => navigate("/users/add")}>
              <Plus size={16} />
              Add User
            </button>
          )}
        </div>
      </div>

      <div className="ems-section-divider" />

      {initialLoading || loadingConfig ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}>
          <CircularProgress />
        </div>
      ) : (
        <TableDataGeneric
          tableName="Users"
          primaryKey="organization_user_id"
          heading="Users"
          data={tableRows}
          sortname="organization_user_id"
          Route="/users"
          DeleteFunc={deleteUser}
              EditFunc={hasEditPermission ? handleEdit : undefined}
              showActions={true}
          token={localStorage.getItem("token")}
          configss={configColumns}
          paginationData={paginationData}
          setPaginationData={setPaginationData}
          onSortChange={handleSortChange}
          onFilterChange={handleFilterChange}
          onPageChange={handlePageChange}
          serverSearchEnabled
          serverSearchValue={searchParams.get("search") || ""}
          onServerSearchChange={handleServerSearchChange}
          filterModule="organization_users"
          loadedBackendConfig={tableConfig}
          edit_delete_action={["USER_EDIT", "USER_DELETE"]}
          loading={fetching || loadingConfig}
          toggleActiveFn={
            hasEditPermission
              ? (row, nextActive) => ToggleStatusTable(row, nextActive)
              : undefined
          }
          {...(tableConfig && { config: tableConfig })}
        />
      )}

      <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this user?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(null)}>Cancel</Button>
          <Button color="error" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default Users;

